import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { shape, object } from "prop-types";
import {
  FormGroup,
  InputGroup,
  HelpBlock,
  Icon,
  Alert
} from "@icg360/ui-toolkit";
import { Button, TextInput, theme } from "@icg360/design-system";
import { track } from "@package/ipcmgr-toolkit";
import { updatePassword, resetPassword } from "../utils/user";
import { configPropTypes } from "../config";
import "./ResetPassword.css";

const RESET_TYPES = {
  UPDATE: "update",
  RESET: "reset"
};

class ResetPassword extends Component {
  resetType =
    new URLSearchParams(window.location.search).has("token") ||
    new URLSearchParams(window.location.search).has("reset-token")
      ? RESET_TYPES.RESET
      : RESET_TYPES.UPDATE;

  els = {};

  static propTypes = {
    config: shape(configPropTypes).isRequired,
    user: object,
    history: object.isRequired
  };

  static defaultProps = {
    user: {}
  };

  state = {
    newPassword: "",
    currentPassword: "",
    isLoading: false,
    validLength: false,
    hasNum: false,
    hasUppercase: false,
    hasLowercase: false,
    hasSpaces: false,
    hasSpecialChar: false,
    doesNotHaveEscapeChar: false,
    showPassword: false,
    success: false,
    error: false
  };

  componentDidMount() {
    this.tryAutoFocus(
      this.resetType === RESET_TYPES.RESET ? "newPassword" : "currentPassword"
    );
    track("Viewed Reset Password");
  }

  saveRef = name => ref => {
    this.els[name] = ref;
  };

  tryAutoFocus = (el = "newPassword") => {
    if (this.els[el]) {
      this.els[el].focus();
    }
  };

  handleInputChange = e => {
    this.setState({
      [e.target.name]: e.target.value
    });
  };

  handleNewPasswordChange = e => {
    const newPassword = e.target.value;
    const validLength = newPassword.length >= 14;
    const hasNum = /\d+/.test(newPassword);
    const specialCharReg = new RegExp("^(?=.*[!@#$%^&*])");
    const uppercaseReg = new RegExp("^(?=.*[A-Z])");
    const lowercaseReg = new RegExp("^(?=.*[a-z])");
    const escapeCharReg = new RegExp("^(?=.*[\"\\'\\\\])");
    const hasUppercase = !!newPassword.match(uppercaseReg);
    const hasLowercase = !!newPassword.match(lowercaseReg);
    const hasSpecialChar = !!newPassword.match(specialCharReg);
    const doesNotHaveEscapeChar = !newPassword.match(escapeCharReg);
    const hasSpaces = !/\s/g.test(newPassword);
    this.setState({
      [e.target.name]: newPassword,
      validLength,
      hasNum,
      hasUppercase,
      hasLowercase,
      hasSpecialChar,
      doesNotHaveEscapeChar,
      hasSpaces
    });
  };

  determineResetEndpoint = () => {
    const {
      config,
      user: { username }
    } = this.props;
    const { currentPassword, newPassword } = this.state;
    return this.resetType === RESET_TYPES.RESET
      ? resetPassword(newPassword, config)
      : updatePassword(username, currentPassword, newPassword, config);
  };

  handleReset = e => {
    e.preventDefault();
    const { user } = this.props;
    this.setState({
      isLoading: true
    });

    track("Clicked Reset Password");

    this.determineResetEndpoint().then(res => {
      if (res.status === "error") {
        this.setState({
          isLoading: false,
          error: res.message
        });
      } else {
        const success = user
          ? "Your password has been reset."
          : "Your password has been reset. You'll automatically be redirected to the login page";
        this.setState({
          isLoading: false,
          newPassword: "",
          validLength: false,
          hasNum: false,
          hasUppercase: false,
          hasLowercase: false,
          hasSpecialChar: false,
          doesNotHaveEscapeChar: false,
          hasSpaces: false,
          success
        });
      }
    });
  };

  showPassword = () => {
    const { showPassword } = this.state;
    this.setState({
      showPassword: !showPassword
    });
  };

  renderCheckbox = checkboxState =>
    checkboxState ? (
      <Icon
        className="reset-password__icon"
        name="check_box"
        style={{ color: theme.colors.fill.default }}
      />
    ) : (
      <Icon
        className="reset-password__icon"
        name="check_box_outline"
        style={{ color: theme.colors.fill.neutralMedium }}
      />
    );

  resetButtonDisabled = () => {
    const {
      isLoading,
      validLength,
      hasNum,
      hasUppercase,
      hasLowercase,
      hasSpecialChar,
      doesNotHaveEscapeChar,
      hasSpaces
    } = this.state;

    return !!(
      isLoading ||
      !(
        validLength &&
        hasNum &&
        hasUppercase &&
        hasSpaces &&
        hasLowercase &&
        hasSpecialChar &&
        doesNotHaveEscapeChar
      )
    );
  };

  render() {
    const {
      isLoading,
      newPassword,
      currentPassword,
      validLength,
      hasNum,
      hasUppercase,
      hasLowercase,
      hasSpecialChar,
      doesNotHaveEscapeChar,
      hasSpaces,
      showPassword,
      success,
      error
    } = this.state;
    const { user, history } = this.props;
    return (
      <div className="reset-password">
        {success && (
          <Alert
            bsStyle="success"
            isGlobal
            autoDismiss
            onDismiss={() => {
              this.setState({ success: false });
              if (Object.entries(user).length === 0) {
                history.replace("/");
              } else {
                history.goBack();
              }
            }}
          >
            <p>
              <strong>Success! </strong>
              {success}
            </p>
          </Alert>
        )}
        {error && (
          <Alert
            bsStyle="danger"
            isGlobal
            autoDismiss
            onDismiss={() => {
              this.setState({ error: false });
            }}
          >
            <p data-bdd="login-failed">
              <strong>Reset failed. </strong>
              {error}
            </p>
          </Alert>
        )}
        <form
          className="reset-password__form container-fluid"
          disabled={isLoading}
          onSubmit={this.handleReset}
        >
          <div className="reset-password__container">
            <div className="reset-password__header">
              <h2>Reset Password</h2>
            </div>
            <div className="reset-password__body">
              {this.resetType === RESET_TYPES.UPDATE && (
                <FormGroup>
                  <TextInput
                    label="Current Password"
                    disabled={isLoading}
                    id="currentPassword"
                    inputRef={this.saveRef("currentPassword")}
                    name="currentPassword"
                    onChange={this.handleInputChange}
                    type="password"
                    value={currentPassword}
                    autoComplete="password"
                  />
                </FormGroup>
              )}

              <FormGroup>
                <InputGroup className="reset-password__search">
                  <TextInput
                    label="New Password"
                    disabled={isLoading}
                    id="newPassword"
                    inputRef={this.saveRef("newPassword")}
                    name="newPassword"
                    onChange={this.handleNewPasswordChange}
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    autoComplete="new-password"
                  />
                  <InputGroup.Button>
                    <Button
                      appearance="secondary"
                      className="reset-password__search--button"
                      onClick={this.showPassword}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </Button>
                  </InputGroup.Button>
                </InputGroup>
                <HelpBlock>
                  Your password must meet the following requirements:{" "}
                  <ul>
                    <li>
                      {this.renderCheckbox(validLength)} At least 14 characters
                    </li>
                    <li>{this.renderCheckbox(hasNum)} A number</li>
                    <li>
                      {this.renderCheckbox(hasUppercase)} An uppercase character
                    </li>
                    <li>
                      {this.renderCheckbox(hasLowercase)} A lowercase character
                    </li>
                    <li>
                      {this.renderCheckbox(hasSpecialChar)} A special character
                    </li>
                    <li>
                      {this.renderCheckbox(doesNotHaveEscapeChar)} None of the
                      following characters: <code>&quot;</code>,{" "}
                      <code>&apos;</code>, <code>\</code>
                    </li>
                    <li>{this.renderCheckbox(hasSpaces)} No spaces</li>
                  </ul>
                </HelpBlock>
              </FormGroup>
            </div>
            <div className="reset-password__footer">
              <Button
                disabled={this.resetButtonDisabled()}
                type="submit"
                loading={isLoading}
                className="pull-right"
              >
                Reset Password
              </Button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}
export default withRouter(ResetPassword);
